<template>
  <div>
    <div v-if="showLocaleSelector">
      <small class="text-uppercase font-weight-bold">
        <br />
        {{ $t("defaultlocale") }}
        <br />
      </small>
      <select v-model="locale">
        <option v-for="(lang, i) in locales" :key="`Lang${i}`" :value="lang">
          {{ getLanguage(lang) }}
        </option>
      </select>
      <br />
      <br />
    </div>

    <div v-if="scenariosByLocale.length">
      <small class="text-uppercase font-weight-bold">
        {{ $t("scenario") }}
      </small>
      <br />
      <select v-model="scenario" @change="onScenarioChange($event)">
        <optgroup label="Connect:">
          <option
            v-for="scenario in classicScenariosByLocale"
            :value="scenarioToKey(scenario)"
            :key="scenarioToKey(scenario)"
          >
            {{ getTitle(scenario) }}
          </option>
        </optgroup>

        <optgroup label="Share:">
          <option
            v-for="scenario in rpsScenariosByLocale"
            :value="scenarioToKey(scenario)"
            :key="scenarioToKey(scenario)"
          >
            {{ getTitle(scenario) }}
          </option>
        </optgroup>
      </select>
      <div v-if="draftsExist">
        <i>{{ $t("draftinfo") }}</i>
      </div>
    </div>
  </div>
</template>

<script>
import { getScenarios, getRPSScenarios } from "@/services/APIService";

const sortby = require("lodash.sortby");

export default {
  data() {
    return {
      scenarios: [],
      rpsScenarios: [],
      scenario: null,
      locale: null,
      locales: []
    };
  },
  watch: {
    getI18nLocale: {
      handler(newData, oldData) {
        // Non practice games have locale selector enabled, so let's do nothing
        if (this.showLocaleSelector) {
          return;
        }

        // For others, update the locale used for scenarios when the $i18n locale is changed
        this.locale = this.$i18n.locale;
        this.scenario = null;
        this.locales = this.$store.state.main.langs;
        this.emitScenario(null, []);
      }
    }
  },
  props: ["showLocaleSelector"],
  computed: {
    draftsExist() {
      for (let i = 0; i < this.rpsScenarios.length; i++) {
        if (this.rpsScenarios[i].draft) {
          return true;
        }
      }

      return false;
    },
    classicScenariosByLocale() {
      if (this.locale === null) {
        return [];
      }
      const classicScenarios = this.scenarios.filter(scenario => scenario.locale === this.locale);

      return this.sortScenarios(classicScenarios);
    },
    rpsScenariosByLocale() {
      if (this.locale === null) {
        return [];
      }
      const rpsScenarios = this.rpsScenarios.filter(scenario => scenario.locale === this.locale);

      return this.sortScenarios(rpsScenarios);
    },
    scenariosByLocale() {
      if (this.locale === null) {
        return [];
      }

      const classicScenarios = this.scenarios.filter(scenario => scenario.locale === this.locale);
      const rpsScenarios = this.rpsScenarios.filter(scenario => scenario.locale === this.locale);

      const scenarios = classicScenarios.concat(rpsScenarios);

      // Let's also sort them:
      return this.sortScenarios(scenarios);
    },
    getI18nLocale() {
      return this.$i18n.locale;
    }
  },
  mounted() {
    this.locale = this.$i18n.locale;

    this.locales = this.$store.state.main.langs;

    this.populateScenarios();
  },

  methods: {
    scenarioToKey(scenario) {
      if (scenario.scenario_id) {
        return "classic " + scenario.scenario_id;
      } else {
        return "rps " + scenario.rps_scenario_id;
      }
    },
    getTitle(scenario, includeType = true) {
      let title = scenario.short_description ? scenario.short_description : scenario.start_title;

      if (scenario.draft) {
        title += " *";
      }

      return title;
    },
    populateScenarios() {
      getScenarios().then(scenarios => {
        this.scenarios = scenarios;
      });
      if (this.$store.state.main.userCreateRPSSimulations) {
        getRPSScenarios().then(rpsScenarios => {
          this.rpsScenarios = rpsScenarios;
        });
      }
    },
    onScenarioChange(event) {
      // Find locales available for this scenario.
      // First do an array of all locales which belong to this scenario:
      const scenarioLocales = [];
      const scenarioVariants = [];
      let selectedScenario = null;

      if (!this.scenario) {
        this.emitScenario(null, []);
        return;
      }

      const array = this.scenario.split(" ");
      const id = parseInt(array[1]);

      if (this.scenario.startsWith("classic")) {
        this.scenarios.forEach(scenario => {
          if (scenario.scenario_id === id) {
            scenarioVariants.push(scenario);
            if (this.locale === scenario.locale) {
              selectedScenario = scenario;
            }

            scenarioLocales.push(scenario.locale);
          }
        });
      } else {
        this.rpsScenarios.forEach(rpsScenario => {
          if (rpsScenario.rps_scenario_id === id) {
            scenarioVariants.push(rpsScenario);
            if (this.locale === rpsScenario.locale) {
              selectedScenario = rpsScenario;
            }

            scenarioLocales.push(rpsScenario.locale);
          }
        });
      }
      this.emitScenario(selectedScenario, scenarioVariants);

      this.locales = [...new Set(scenarioLocales)];
    },
    sortScenarios(scenarios) {
      return sortby(scenarios, [
        o => {
          return this.getTitle(o, false).toLowerCase();
        }
      ]);
    },
    emitScenario(selectedScenario, scenarioVariants = []) {
      this.$bus.$emit(
        "scenario-selector-scenario-selected",
        selectedScenario,
        scenarioVariants
      );
    },
    getLanguage(lang) {
      // Hard coded for now for simplicity
      switch (lang) {
        case "en":
          return "ENGLISH";
        case "fi":
          return "SUOMI";
        case "de":
          return "DEUTSCH";
        case "sv":
          return "SVENSKA";
        case "ko":
          return "한국어";
        default:
          return lang;
      }
    }
  }
};
</script>

<i18n>
{
  "en": {
    "defaultlocale": "Default language",
    "scenario": "* Simulation",
    "draftinfo": "Simulations marked with * are not published yet. Unpublished simulations can only be accessed by you and other facilitators in your organization, who have scenario editing rights. You can publish the simulation for your whole organization in the simulation editor."
  },
  "fi": {
    "defaultlocale": "Oletuskieli",
    "scenario": "* Simulaatio",
    "draftinfo": "Simulaatioita, jotka on merkitty tähdellä *, ei ole vielä julkaistu. Julkaisemattomia simulaatioita voivat hyödyntää vain muokkausoikeuden omaavat fasilitoijat. Kun haluat simulaation olevan kaikkien organisaatiosi jäsenten käytettävissä, julkaise simulaatio simulaatioeditorissa."
  },
 "sv": {
    "defaultlocale": "Förvalt språk",
    "scenario": "* Simuleringen"
  },
 "de": {
    "defaultlocale": "Standard-Sprache",
    "scenario": "* Simulation"
  },
"ko": {
    "defaultlocale": "언어 설정",
    "scenario": "* 시뮬레이션"
  }
}
</i18n>
